import { Field } from '@/components/Fields';
import { useAppDispatch, useErrorIndicator } from '@/hooks';
import {
  Button,
  Center,
  Flex,
  FormControl,
  Heading,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import Link from 'next/link';
import { Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useState } from 'react';
import * as Yup from 'yup';
import { reset } from './actions';

interface ResetPasswordRequest {
  newPassword: string;
}

export const ResetPasswordView: React.FC = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const toast = useToast();
  const { showError } = useErrorIndicator();
  const forgotSchema = Yup.object().shape({
    newPassword: Yup.string().min(8, 'Too Short!').max(60, 'Too Long!').required('Required'),
  });
  const { token } = router.query;
  const handleSubmit = async (
    values: ResetPasswordRequest,
    { setSubmitting }: FormikHelpers<ResetPasswordRequest>,
  ) => {
    if (token) {
      setSubmitting(true);
      dispatch(
        reset({
          formFields: [
            {
              id: 'password',
              value: values.newPassword,
            },
          ],
          method: 'token',
          token: String(token),
        }),
      )
        .unwrap()
        .then((response: any) => {
          setSubmitting(false);
          if (response.status === 'FIELD_ERROR') {
            response.formFields.forEach((formField: any) => {
              if (formField.id === 'password') {
                setErrorMessage(formField.error);
              }
            });
          } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
            router.push('/forgot');
            showError('Reset password link expired');
          } else {
            router.push('/');
            toast({
              title: 'Successfully changed password',
              description: 'You can now sign in using new password',
              status: 'success',
              duration: 5000,
              isClosable: false,
            });
          }
        })
        .catch((err) => {
          if (err.isSuperTokensGeneralError === true) {
            setErrorMessage(err.message);
          } else {
            setErrorMessage('Oops! Something went wrong.');
          }
        });
    } else {
    }
  };

  return (
    <Center>
      <VStack gap={12}>
        <Heading>Reset password</Heading>
        <Formik
          initialValues={{ newPassword: '' }}
          onSubmit={handleSubmit}
          validationSchema={forgotSchema}
        >
          {({ handleSubmit, isSubmitting, isValid, dirty, errors, touched }) => (
            <Form onSubmit={handleSubmit}>
              <FormControl alignItems="center" display="flex" flexDir="column">
                <Field
                  type="password"
                  onDark
                  placeholder="New password"
                  name="newPassword"
                  errors={errors.newPassword}
                  touched={touched.newPassword}
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  h="56px"
                  pl={3}
                  autoComplete="new-password"
                  full
                />
                {!!errorMessage && (
                  <Text mb="30px" color="red" justifySelf="flex-start">
                    {errorMessage}
                  </Text>
                )}
                <Button
                  fontWeight="semibold"
                  variant="primary"
                  size="xl"
                  w="full"
                  minW={{ base: 'unset', sm: '350px' }}
                  maxW="350px"
                  type="submit"
                  isDisabled={!isValid || !dirty || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Set new password
                </Button>
              </FormControl>
            </Form>
          )}
        </Formik>
        <Flex gap={1} flexDir={{ base: 'column', sm: 'row' }}>
          <Text>Want to sign in using old password?</Text>
          <Text as={Link} href="/" color="teal.500" cursor="pointer">
            Log in
          </Text>
        </Flex>
      </VStack>
    </Center>
  );
};
